import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { LinkButton } from "../../forms/Button/LinkButton";
import { CloseIcon } from "../../icons/CloseIcon";
import { ErrorIcon } from "../../icons/ErrorIcon";
import { SuccessIcon } from "../../icons/SuccessIcon";
import { WarningIcon } from "../../icons/WarningIcon";
import { classNames } from "../../utils/classNames";
import { LoadingSpinner } from "../LoadingSpinner";

const closeToastLabel = msg({
	message: "Close Toast",
	comment: "Accessible name of the X button that dismisses a toast notification",
});

const alertIcons = {
	error: <ErrorIcon className="h-6 w-6 text-toast-critical" aria-hidden="true" />,
	success: <SuccessIcon className="h-6 w-6 text-toast-success" aria-hidden="true" />,
	warning: <WarningIcon className="h-6 w-6 text-toast-warning" aria-hidden="true" />,
	loading: <LoadingSpinner size="sm" className="text-white" aria-hidden="true" />,
};

export type ToastProps = {
	title: string;
	description?: string | React.JSX.Element;
	variant: "success" | "error" | "warning" | "loading";
	action?: { label: string; onClick: () => void };
	closeToast?: () => void;
};

export const Toast = ({ title, description, variant, action, closeToast }: ToastProps) => {
	const { i18n } = useLingui();
	return (
		<div className="w-full p-4">
			<div className="flex items-start">
				<div className="shrink-0">{alertIcons[variant]}</div>
				<div
					className={classNames(
						"ml-3 flex w-0 flex-1 justify-between gap-3",
						description ? "flex-col items-start" : "",
					)}
				>
					<div className="flex flex-col gap-1 pt-0.5">
						<div className="flex flex-col">
							<p className="font-medium text-sm text-white">{title}</p>
							{description && <p className="text-on-neutral text-xs">{description}</p>}
						</div>
						{action && (
							<LinkButton
								type="button"
								className="self-start text-white text-xs hover:text-white"
								onClick={() => {
									action.onClick();
									closeToast?.();
								}}
							>
								{action.label}
							</LinkButton>
						)}
					</div>
				</div>

				<div className="ml-4 flex shrink-0">
					<button
						onClick={closeToast}
						type="button"
						className="inline-flex rounded-md bg-transparent text-white hover:opacity-80 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-on-neutral focus-visible:ring-offset-2 focus-visible:ring-offset-neutral"
						aria-label={i18n._(closeToastLabel)}
					>
						<CloseIcon className="h-5 w-5" aria-hidden="true" />
					</button>
				</div>
			</div>
		</div>
	);
};
